import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #checkedIndices = [];
  #radios = [];
  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** bind once */
  #onChange = (event) => this.variantChanged(event);

  connectedCallback() {
    super.connectedCallback();

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios.length = 0;
    this.#checkedIndices.length = 0;

    for (let i = 0; i < fieldsets.length; i++) {
      const radios = Array.from(fieldsets[i].querySelectorAll('input'));
      this.#radios.push(radios);

      const index = radios.findIndex((r) => r.dataset.currentChecked === 'true');
      if (index !== -1) this.#checkedIndices.push([index]);
    }

    this.addEventListener('change', this.#onChange);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('change', this.#onChange);
  }

  variantChanged(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const selectedOption =
      target instanceof HTMLSelectElement
        ? target.options[target.selectedIndex]
        : target;

    if (!selectedOption) return;

    this.updateSelectedOption(target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId || '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphSelector);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId;

    if (isOnProductPage) {
      variantId ? url.searchParams.set('variant', variantId) : url.searchParams.delete('variant');
    }

    if (loadsNewProduct) url.pathname = newUrl;

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => history.replaceState({}, '', url.toString()));
    }
  }

  #getFieldsetMeasurements(index) {
    const fieldset = this.refs.fieldsets?.[index];
    const radios = this.#radios[index];
    const checked = this.#checkedIndices[index];

    if (!fieldset || !radios || !checked) return null;

    const [current, previous] = checked;

    return {
      fieldset,
      currentIndex: current,
      previousIndex: previous,
      currentWidth: current !== undefined ? radios[current]?.parentElement?.offsetWidth : undefined,
      previousWidth: previous !== undefined ? radios[previous]?.parentElement?.offsetWidth : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    currentWidth
      ? fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`)
      : currentIndex !== undefined && fieldset.style.removeProperty('--pill-width-current');

    previousWidth
      ? fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`)
      : previousIndex !== undefined && fieldset.style.removeProperty('--pill-width-previous');
  }

  updateFieldsetCss(index) {
    const m = this.#getFieldsetMeasurements(index);
    if (m) this.#applyFieldsetMeasurements(m);
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      target = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!target) throw new Error('Target element not found');
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number(target.dataset.fieldsetIndex);
      const inputIndex = Number(target.dataset.inputIndex);

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const radios = this.#radios[fieldsetIndex];
        const checked = this.#checkedIndices[fieldsetIndex];

        if (radios && checked) {
          const [current, previous] = checked;

          current !== undefined && radios[current] && (radios[current].dataset.currentChecked = 'false');
          previous !== undefined && radios[previous] && (radios[previous].dataset.previousChecked = 'false');

          checked.unshift(inputIndex);
          checked.length = Math.min(checked.length, 2);

          radios[inputIndex].dataset.currentChecked = 'true';
          if (checked[1] !== undefined) radios[checked[1]].dataset.previousChecked = 'true';

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const option = target.options[target.selectedIndex];
      if (!option) throw new Error('Option not found');

      for (const o of target.options) o.removeAttribute('selected');
      option.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceValues = []) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

    const params = [];
    const view = getViewParameterValue();
    if (view) params.push(`view=${view}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      params.push(`option_values=${sourceValues.join(',') || selectedOption.dataset.optionValueId}`);
    }

    const map = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const key = Object.keys(map).find((k) => this.closest(k));
    if (key) return `${productUrl.split('?')[0]}?section_id=${map[key]}&${params.join('&')}`;

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(url, selector) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(url, { signal: this.#abortController.signal })
      .then((r) => r.text())
      .then((text) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(text, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const json = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!json) return;

        if (selector === 'main') this.updateMain(html);
        else if (selector) this.updateElement(html, selector);
        else {
          const newProduct = this.updateVariantPicker(html);
          this.selectedOptionId &&
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(json), this.selectedOptionId, {
                html,
                productId: this.dataset.productId || '',
                newProduct,
              })
            );
        }
      })
      .catch((e) => e.name !== 'AbortError' && console.error(e));
  }

  updateVariantPicker(html) {
    const source = html.querySelector(this.tagName.toLowerCase());
    if (!source) throw new Error('No new variant picker source found');

    if (source instanceof HTMLElement) {
      this.dataset.productId = source.dataset.productId;
      this.dataset.productUrl = source.dataset.productUrl;
    }

    morph(this, source, {
      ...MORPH_OPTIONS,
      getNodeKey: (n) => n instanceof HTMLElement && n.dataset.key,
    });

    this.updateVariantPickerCss();
  }

  updateVariantPickerCss() {
    const fieldsets = this.refs.fieldsets || [];
    const measurements = fieldsets.map((_, i) => this.#getFieldsetMeasurements(i)).filter(Boolean);
    measurements.forEach((m) => this.#applyFieldsetMeasurements(m));
  }

  updateElement(html, selector) {
    const el = this.closest(selector);
    const src = html.querySelector(selector);
    if (!el || !src) throw new Error(`No source found for ${selector}`);
    morph(el, src);
  }

  updateMain(html) {
    const main = document.querySelector('main');
    const src = html.querySelector('main');
    if (!main || !src) throw new Error('No main source found');
    morph(main, src);
  }

  get selectedOption() {
    const el = this.querySelector('select option[selected], fieldset input:checked');
    return el instanceof HTMLInputElement || el instanceof HTMLOptionElement ? el : undefined;
  }

  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId;
  }

  get selectedOptionsValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      (o) => o.dataset.optionValueId
    );
  }
}

customElements.get('variant-picker') || customElements.define('variant-picker', VariantPicker);
